// LAP-550 V1.2 — Layout commun pour les pages détail (Top Nav + Chart + Table)
// Inspiré du dashboard "Mes revenus" : currency / date / view / settings → chart → table.
import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Container,
  Stack,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Popover,
  Switch,
  Divider,
  Button,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import LegendToggleIcon from '@mui/icons-material/LegendToggle'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import TableRowsIcon from '@mui/icons-material/TableRows'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { makeGetRequest, makePatchRequest } from 'utils/api'
import { invTheme } from './theme'

const DEFAULT_SETTINGS = {
  currency: 'EUR',
  date_range: '1Y',
  body_view: 'both',
  legend_on: true,
}

/**
 * DetailPageLayout — coque générique pour toutes les pages détail.
 *
 * Props attendues :
 *  - title, subtitle       Titre/sous-titre du header
 *  - icon                  Élément React (icône MUI) ou chemin string
 *  - accent                Couleur d'accent (cf. invTheme.accents)
 *  - chartSubtitleOptions  [{ value, label }] — segmenter chart (id 7 du spec)
 *  - chartSubtitle / setChartSubtitle
 *  - sumLabel, sumValue    Sum affiché à droite (id 12)
 *  - chart                 Élément React (Line/Bar/Doughnut)
 *  - tableTitle, tableCount, tableContent
 *  - actions               Boutons custom à droite du header (id 15)
 *  - backTo                Route vers laquelle revenir au clic "back" (default '/investments')
 */
export default function DetailPageLayout({
  title,
  subtitle,
  // Surcharge optionnelle du grand titre. Par défaut toutes les pages détail
  // affichent "Mes investissements" et relèguent title/subtitle en eyebrow ;
  // la fiche d'une valeur unique met plutôt le nom de la valeur en avant.
  pageTitleOverride,
  icon,
  accent,
  chartSubtitleOptions,
  chartSubtitle,
  onChartSubtitleChange,
  sumLabel,
  sumValue,
  chart,
  tableTitle,
  tableCount,
  tableContent,
  actions,
  backTo = '/investments',
  prevNext, // { onPrev, onNext, prevLabel, nextLabel }
}) {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const [currency, setCurrency] = useState('EUR')
  const [dateRange, setDateRange] = useState('1Y')
  const [bodyView, setBodyView] = useState('both') // 'chart' | 'table' | 'both'
  const [legendOn, setLegendOn] = useState(true)
  const [settingsAnchor, setSettingsAnchor] = useState(null)
  // Garde-fou : on ne persiste pas pendant le chargement initial des réglages.
  const loadedRef = useRef(false)
  const rootRef = useRef(null)

  // Le titre du header est désormais commun à toutes les pages détail.
  // Le titre spécifique de la card devient un sous-titre (eyebrow).
  const pageTitle = pageTitleOverride || t('investments.detail.pageTitle', 'Mes investissements')
  const eyebrow = [title, subtitle].filter(Boolean).join(' · ')
  // Les contrôles liés au graphique (légendes, bascule graphique/tableau)
  // n'ont aucun sens quand la page n'affiche pas de graphique.
  const hasChart = Boolean(chart)

  // Chargement des préférences d'affichage persistées de l'utilisateur.
  useEffect(() => {
    const load = async () => {
      const { data, status } = await makeGetRequest(
        '/api/investment/detail-settings/',
        {},
        true,
      )
      if (status === 200 && data) {
        if (data.currency) setCurrency(data.currency)
        if (data.date_range) setDateRange(data.date_range)
        if (data.body_view) setBodyView(data.body_view)
        if (typeof data.legend_on === 'boolean') setLegendOn(data.legend_on)
      }
      loadedRef.current = true
    }
    load()
  }, [])

  // Persistance backend des réglages (PATCH partiel, silencieux).
  const persist = (partial) => {
    if (!loadedRef.current) return
    makePatchRequest('/api/investment/detail-settings/', partial, {}, true)
  }
  const updateCurrency = (v) => {
    setCurrency(v)
    persist({ currency: v })
  }
  const updateDateRange = (v) => {
    setDateRange(v)
    persist({ date_range: v })
  }
  const updateBodyView = (v) => {
    setBodyView(v)
    persist({ body_view: v })
  }
  const updateLegend = (v) => {
    setLegendOn(v)
    persist({ legend_on: v })
  }
  const resetSettings = () => {
    setCurrency(DEFAULT_SETTINGS.currency)
    setDateRange(DEFAULT_SETTINGS.date_range)
    setBodyView(DEFAULT_SETTINGS.body_view)
    setLegendOn(DEFAULT_SETTINGS.legend_on)
    persist(DEFAULT_SETTINGS)
  }

  const toggleFullscreen = () => {
    const el = rootRef.current
    if (!el) return
    if (document.fullscreenElement) document.exitFullscreen?.()
    else el.requestFullscreen?.()
  }

  const renderIcon = () => {
    if (!icon) return null
    if (typeof icon === 'string') {
      return (
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <img src={icon} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
        </Box>
      )
    }
    return (
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          backgroundColor: 'rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1.5,
          color: '#cfe1ff',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    )
  }

  return (
    <Box ref={rootRef} sx={{ width: '100%', color: 'white' }}>
      <Container maxWidth="xl">
        {/* Header — back + icon + title + sum + actions */}
        <Box display="flex" alignItems="center" sx={{ pt: 2, pb: 1.5 }}>
          <Tooltip title={t('investments.detail.back', 'Retour')}>
            <IconButton onClick={() => navigate(backTo)} sx={{ color: 'rgba(255,255,255,0.85)', mr: 1 }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {renderIcon()}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{pageTitle}</Box>
            {eyebrow ? (
              <Box sx={{ ...invTheme.subtitle, mt: 0.5 }}>{eyebrow}</Box>
            ) : null}
          </Box>
          {prevNext && (
            <Stack direction="row" spacing={0.5} sx={{ mr: 1 }}>
              <Tooltip title={prevNext.prevLabel || ''}>
                <IconButton size="small" onClick={prevNext.onPrev} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={prevNext.nextLabel || ''}>
                <IconButton size="small" onClick={prevNext.onNext} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 14, transform: 'rotate(180deg)' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          {actions ? <Box sx={{ ml: 'auto' }}>{actions}</Box> : null}
        </Box>

        {/* Top Navigation Bar — controls (ids 1-8 of spec) */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 2,
            border: invTheme.cardBorder,
            background: 'linear-gradient(180deg, rgba(7,40,82,0.6), rgba(0,18,44,0.6))',
            mb: 2,
          }}
        >
          {/* 1. Currency selector */}
          <Select
            size="small"
            value={currency}
            onChange={(e) => updateCurrency(e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="EUR">€ EUR</MenuItem>
            <MenuItem value="USD">$ USD</MenuItem>
            <MenuItem value="GBP">£ GBP</MenuItem>
            <MenuItem value="CHF">CHF</MenuItem>
          </Select>
          {/* 2. Date selector */}
          <Select
            size="small"
            value={dateRange}
            onChange={(e) => updateDateRange(e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="1M">1 {t('investments.detail.month', 'mois')}</MenuItem>
            <MenuItem value="3M">3 {t('investments.detail.months', 'mois')}</MenuItem>
            <MenuItem value="1Y">1 {t('investments.detail.year', 'an')}</MenuItem>
            <MenuItem value="3Y">3 {t('investments.detail.years', 'ans')}</MenuItem>
            <MenuItem value="ALL">{t('investments.detail.all', 'Tout')}</MenuItem>
          </Select>
          {/* Légendes — visible uniquement quand un graphique est affiché */}
          {hasChart && (
            <Tooltip title={t('investments.detail.legends', 'Légendes')}>
              <IconButton
                size="small"
                onClick={() => updateLegend(!legendOn)}
                sx={{ color: legendOn ? '#63a0ff' : 'rgba(255,255,255,0.55)' }}
              >
                <LegendToggleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* Plein écran */}
          <Tooltip title={t('investments.detail.fullscreen', 'Plein écran')}>
            <IconButton
              size="small"
              onClick={toggleFullscreen}
              sx={{ color: 'rgba(255,255,255,0.65)' }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Bascule graphique / tableau — seulement si un graphique existe */}
          {hasChart && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={bodyView}
              onChange={(e, v) => v && updateBodyView(v)}
              sx={{
                ml: 'auto',
                '.MuiToggleButton-root': {
                  color: 'rgba(255,255,255,0.6)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  py: 0.25,
                },
                '.Mui-selected': { color: '#63a0ff !important', backgroundColor: 'rgba(99,160,255,0.12) !important' },
              }}
            >
              <ToggleButton value="chart" aria-label="chart">
                <ShowChartIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="both" aria-label="both">
                <Box sx={{ fontSize: 11, px: 0.5 }}>{t('investments.detail.both', 'Tout')}</Box>
              </ToggleButton>
              <ToggleButton value="table" aria-label="table">
                <TableRowsIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Tooltip title={t('investments.detail.settings', 'Réglages')}>
            <IconButton
              size="small"
              onClick={(e) => setSettingsAnchor(e.currentTarget)}
              sx={{
                ml: hasChart ? 0 : 'auto',
                color: settingsAnchor ? '#63a0ff' : 'rgba(255,255,255,0.65)',
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Panneau de réglages d'affichage — préférences persistées */}
        <Popover
          open={Boolean(settingsAnchor)}
          anchorEl={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              width: 280,
              p: 2,
              color: 'white',
              background: 'linear-gradient(180deg, #0a2c59, #051a38)',
              border: '1px solid rgba(255,255,255,0.12)',
            },
          }}
        >
          <Box sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
            {t('investments.detail.settingsPanelTitle', 'Réglages d’affichage')}
          </Box>
          <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', mb: 1.5 }}>
            {t('investments.detail.settingsSavedHint', 'Vos préférences sont enregistrées automatiquement.')}
          </Box>

          <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
            {t('investments.detail.currencyLabel', 'Devise')}
          </Box>
          <Select
            size="small"
            fullWidth
            value={currency}
            onChange={(e) => updateCurrency(e.target.value)}
            sx={{ ...selectSx, mb: 1.5 }}
          >
            <MenuItem value="EUR">€ EUR</MenuItem>
            <MenuItem value="USD">$ USD</MenuItem>
            <MenuItem value="GBP">£ GBP</MenuItem>
            <MenuItem value="CHF">CHF</MenuItem>
          </Select>

          <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
            {t('investments.detail.periodLabel', 'Période')}
          </Box>
          <Select
            size="small"
            fullWidth
            value={dateRange}
            onChange={(e) => updateDateRange(e.target.value)}
            sx={{ ...selectSx, mb: 1.5 }}
          >
            <MenuItem value="1M">1 {t('investments.detail.month', 'mois')}</MenuItem>
            <MenuItem value="3M">3 {t('investments.detail.months', 'mois')}</MenuItem>
            <MenuItem value="1Y">1 {t('investments.detail.year', 'an')}</MenuItem>
            <MenuItem value="3Y">3 {t('investments.detail.years', 'ans')}</MenuItem>
            <MenuItem value="ALL">{t('investments.detail.all', 'Tout')}</MenuItem>
          </Select>

          {hasChart && (
            <>
              <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
                {t('investments.detail.displayLabel', 'Affichage')}
              </Box>
              <Select
                size="small"
                fullWidth
                value={bodyView}
                onChange={(e) => updateBodyView(e.target.value)}
                sx={{ ...selectSx, mb: 1 }}
              >
                <MenuItem value="chart">{t('investments.detail.viewChart', 'Graphique')}</MenuItem>
                <MenuItem value="both">{t('investments.detail.both', 'Tout')}</MenuItem>
                <MenuItem value="table">{t('investments.detail.viewTable', 'Tableau')}</MenuItem>
              </Select>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  {t('investments.detail.legends', 'Légendes')}
                </Box>
                <Switch
                  size="small"
                  checked={legendOn}
                  onChange={(e) => updateLegend(e.target.checked)}
                />
              </Box>
            </>
          )}

          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.12)' }} />
          <Button
            fullWidth
            size="small"
            onClick={resetSettings}
            sx={{
              textTransform: 'none',
              color: 'rgba(255,255,255,0.8)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}
          >
            {t('investments.detail.resetSettings', 'Réinitialiser les réglages')}
          </Button>
        </Popover>

        {/* Segmenter de sous-vue — toujours visible quand il y a des options,
            même sans chart (cas des pages détail Marché). */}
        {chartSubtitleOptions && chartSubtitleOptions.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1,
              borderRadius: invTheme.cardRadius,
              border: invTheme.cardBorder,
              background: 'linear-gradient(180deg, rgba(7,40,82,0.5), rgba(0,18,44,0.5))',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={chartSubtitle}
              onChange={(e, v) => v && onChartSubtitleChange && onChartSubtitleChange(v)}
              sx={{
                flexWrap: 'wrap',
                '.MuiToggleButton-root': {
                  color: 'rgba(255,255,255,0.65)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  fontSize: 11,
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                },
                '.Mui-selected': {
                  color: '#001b42 !important',
                  backgroundColor: `${accent || '#cfe1ff'} !important`,
                },
              }}
            >
              {chartSubtitleOptions.map((o) => (
                <ToggleButton key={o.value} value={o.value}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {(sumLabel || sumValue !== undefined) && (
              <Box sx={{ ml: 'auto', textAlign: 'right', pr: 1 }}>
                {sumLabel && (
                  <Box sx={{ ...invTheme.subtitle, color: 'rgba(255,255,255,0.55)' }}>{sumLabel}</Box>
                )}
                {sumValue !== undefined && sumValue !== null && (
                  <Box sx={{ fontSize: 18, fontWeight: 700 }}>{sumValue}</Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Chart section */}
        {(bodyView === 'chart' || bodyView === 'both') && chart && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: invTheme.cardRadius,
              border: invTheme.cardBorder,
              background: invTheme.cardBg,
              position: 'relative',
              boxShadow: invTheme.cardShadow,
              '&::before': accent
                ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 16,
                    bottom: 16,
                    width: 3,
                    borderRadius: 2,
                    background: accent,
                  }
                : undefined,
              pl: accent ? 3 : 2,
            }}
          >
            {/* Si pas de segmenter au-dessus, afficher le sum dans le chart */}
            {!chartSubtitleOptions && (sumLabel || sumValue !== undefined) && (
              <Box display="flex" alignItems="flex-start" mb={1.5}>
                <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                  {sumLabel && (
                    <Box sx={{ ...invTheme.subtitle, color: 'rgba(255,255,255,0.55)' }}>{sumLabel}</Box>
                  )}
                  {sumValue !== undefined && sumValue !== null && (
                    <Box sx={{ fontSize: 22, fontWeight: 700 }}>{sumValue}</Box>
                  )}
                </Box>
              </Box>
            )}
            <Box sx={{ height: 280 }}>{chart}</Box>
          </Box>
        )}

        {/* Table section — toujours visible quand il n'y a pas de graphique */}
        {(!hasChart || bodyView === 'table' || bodyView === 'both') && (
          <Box
            sx={{
              p: 2,
              borderRadius: invTheme.cardRadius,
              border: invTheme.cardBorder,
              background: invTheme.cardBg,
              boxShadow: invTheme.cardShadow,
              mb: 4,
            }}
          >
            <Box display="flex" alignItems="baseline" mb={1.5}>
              <Box sx={invTheme.title}>{tableTitle}</Box>
              {tableCount !== undefined && tableCount !== null && (
                <Box
                  sx={{
                    ml: 1.25,
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.55)',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 999,
                  }}
                >
                  {tableCount}
                </Box>
              )}
            </Box>
            {tableContent}
          </Box>
        )}
      </Container>
    </Box>
  )
}

const selectSx = {
  fontSize: 12,
  color: 'rgba(255,255,255,0.85)',
  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,160,255,0.4)' },
  '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  minWidth: 90,
}
